/**
 * @file     useViewedListings.ts
 * @purpose  Tracks unique listing identifiers (slugOrKey) viewed by an
 *           unauthenticated visitor in localStorage. Feeds the auth gate
 *           modal on the browse page and the inline premium-panel gates on
 *           listing details. Public listing content remains readable.
 */
'use client';

import { useCallback, useEffect, useState } from 'react';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { reltaTrack } from '@/hooks/useReltaTrack';

// v2 intentionally resets the old all-arrivals counter. Its entries were
// collected before internal-only attribution and cannot be safely migrated.
const STORAGE_KEY = 'relta.viewed_listings.v2';
const THRESHOLD_FIRED_KEY = 'relta.viewed_listings.v2.threshold_fired';
// Number of distinct listing views before unauthenticated visitors lose
// access to premium intelligence panels (the Read, Market Story, Supply
// Outlook, Area Mix, Demographics) on the listing detail page. Browsing
// the basic listing data + gallery + mortgage calculator stays free
// indefinitely. Only the "extra data + analytics" panels are gated.
//
// Only listing pages reached through Relta browsing count. Direct, email,
// search-engine, and other external arrivals remain public and do not advance
// this threshold.
export const VIEWED_LISTINGS_THRESHOLD = 10;
const STORAGE_EVENT = 'relta:viewed-listings-changed';

function readSet(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch (err) {
    reportSilentFailure(err, { area: 'auth-gate/read-viewed' });
    return [];
  }
}

function writeSet(values: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
  } catch (err) {
    reportSilentFailure(err, { area: 'auth-gate/write-viewed' });
  }
}

/** Distinct listings this browser has recorded. Exported so the shared gate
 *  floor in `useListingsAccess` can count them alongside its own store — a
 *  visitor's "properties seen" spans both surfaces. */
export function readViewedListingKeys(): string[] {
  return readSet();
}

export function useViewedListings() {
  const [keys, setKeys] = useState<string[]>(() => readSet());

  useEffect(() => {
    const sync = () => setKeys(readSet());
    window.addEventListener(STORAGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(STORAGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const record = useCallback((key: string | null | undefined) => {
    if (!key) return;
    const current = readSet();
    if (current.includes(key)) return;
    const next = [...current, key];
    writeSet(next);
    // Fire threshold_reached exactly once per browser when the count first
    // crosses the threshold. Persisted flag so a page refresh doesn't
    // re-fire it; cleared only on signup (auth.signin path) so a
    // re-anonymized visitor would re-trigger.
    if (
      next.length === VIEWED_LISTINGS_THRESHOLD &&
      typeof window !== 'undefined' &&
      !window.localStorage.getItem(THRESHOLD_FIRED_KEY)
    ) {
      try {
        window.localStorage.setItem(THRESHOLD_FIRED_KEY, '1');
        reltaTrack('auth_gate.threshold_reached', {
          threshold: VIEWED_LISTINGS_THRESHOLD,
          viewed_count: next.length,
        });
      } catch {
        // Analytics is fire-and-forget.
      }
    }
  }, []);

  return {
    count: keys.length,
    hasReachedThreshold: keys.length >= VIEWED_LISTINGS_THRESHOLD,
    record,
  };
}
